import { Children, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { readLocalValue, removeLocalValue, writeLocalValue } from '../lib/browserStorage.js';

const HANDLE_WIDTH = 12;
const KEY_STEP = 24;

/** Presentation preference only: no content mutation, activity kind or unsaved-work probe. */
export function ReviewColumns({
  children, viewerId, page, defaultLeftWidth, defaultLeftRatio = 0.45,
  minLeftWidth = 360, minRightWidth = 320, fullscreen = false,
}: {
  children: ReactNode;
  viewerId?: string | undefined;
  page: 'practice' | 'lesson';
  defaultLeftWidth?: number;
  defaultLeftRatio?: number;
  minLeftWidth?: number;
  minRightWidth?: number;
  fullscreen?: boolean;
}) {
  const storageKey = viewerId?.trim() ? `author.review-columns.${page}.${encodeURIComponent(viewerId)}` : null;
  const readRatio = () => {
    const raw = storageKey ? readLocalValue(storageKey) : null;
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) && value > 0 && value < 1 ? value : null;
  };
  const [ratio, setRatio] = useState<number | null>(readRatio);
  const ratioRef = useRef(ratio);
  const rootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(() => typeof window === 'undefined' ? 1400 : window.innerWidth - 24);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);

  useLayoutEffect(() => {
    const next = readRatio();
    ratioRef.current = next;
    setRatio(next);
    // A new viewer never inherits the preceding viewer's preferred width.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const measure = () => {
      const rect = root.getBoundingClientRect();
      if (rect.width <= 0) return; // not measured yet (also the jsdom default)
      const css = getComputedStyle(root);
      const padding = (parseFloat(css.paddingLeft) || 0) + (parseFloat(css.paddingRight) || 0);
      setWidth(Math.max(0, rect.width - padding));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(root);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const stacked = width < minLeftWidth + minRightWidth + HANDLE_WIDTH;
  const available = Math.max(minLeftWidth + minRightWidth, width - HANDLE_WIDTH);
  const maxLeft = available - minRightWidth;
  const clamp = (value: number) => Math.round(Math.min(maxLeft, Math.max(minLeftWidth, value)));
  const leftWidth = clamp(ratio === null ? defaultLeftWidth ?? available * defaultLeftRatio : available * ratio);
  const update = (next: number, persist: boolean) => {
    const value = clamp(next) / available;
    ratioRef.current = value;
    setRatio(value);
    if (persist && storageKey) writeLocalValue(storageKey, String(value));
  };
  const reset = () => {
    ratioRef.current = null;
    setRatio(null);
    if (storageKey) removeLocalValue(storageKey);
  };
  const finish = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (storageKey && ratioRef.current !== null) writeLocalValue(storageKey, String(ratioRef.current));
    try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch { /* browser already released capture */ }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = event.key === 'ArrowLeft' ? leftWidth - KEY_STEP
      : event.key === 'ArrowRight' ? leftWidth + KEY_STEP
        : event.key === 'Home' ? minLeftWidth : event.key === 'End' ? maxLeft : null;
    if (event.key === 'Enter') { event.preventDefault(); reset(); }
    else if (next !== null) { event.preventDefault(); update(next, true); }
  };
  const panes = Children.toArray(children);
  // Stable wrappers are essential: stacking must not restart the learner's run or lose drafts.
  const paneStyle = {
    display: 'grid', minWidth: 0, minHeight: 0,
    gridTemplateColumns: 'minmax(0, 1fr)', gridTemplateRows: 'minmax(0, 1fr)',
    pointerEvents: dragging ? 'none' as const : undefined,
  };
  return (
    <div
      className="grid min-h-0 flex-1 p-3"
      data-testid="review-columns" data-stacked={stacked ? 'true' : 'false'} ref={rootRef}
      style={{
        gridTemplateColumns: stacked ? 'minmax(0, 1fr)' : `${leftWidth}px ${HANDLE_WIDTH}px minmax(0, 1fr)`,
        gridTemplateRows: stacked ? 'minmax(28rem, 1fr) minmax(28rem, 1fr)' : 'minmax(0, 1fr)',
        overflowY: stacked ? 'auto' : undefined,
        rowGap: stacked ? 12 : undefined,
      }}
    >
      <div style={paneStyle}>{panes[0]}</div>
      <div
        hidden={stacked || fullscreen}
        aria-label="Resize review columns" aria-orientation="vertical"
        aria-valuemin={minLeftWidth} aria-valuemax={Math.round(maxLeft)} aria-valuenow={leftWidth}
        aria-valuetext={`${leftWidth} pixels`} role="separator" tabIndex={stacked || fullscreen ? -1 : 0}
        className="flex cursor-col-resize touch-none items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700"
        // Tailwind's display:flex can override the browser's hidden rule.
        style={{ display: stacked || fullscreen ? 'none' : undefined }}
        title="Drag to resize columns. Arrow keys adjust width; Enter or double-click resets."
        onDoubleClick={reset} onKeyDown={onKeyDown}
        onPointerDown={(event) => {
          if (event.button !== 0 || stacked || fullscreen) return;
          event.preventDefault();
          try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { return; }
          drag.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: leftWidth };
          setDragging(true);
        }}
        onPointerMove={(event) => {
          const current = drag.current;
          if (current?.pointerId === event.pointerId) update(current.startWidth + event.clientX - current.startX, false);
        }}
        onPointerUp={finish} onPointerCancel={finish}
        onLostPointerCapture={(event) => { finish(event); }}
      >
        <span aria-hidden="true" className="h-16 w-1 rounded-full bg-slate-300" />
      </div>
      <div style={paneStyle}>{panes[1]}</div>
    </div>
  );
}
